const post = {
  meta: {
    slug: "does-checking-your-rate-hurt-your-credit",
    title: "Does Checking Your Rate Hurt Your Credit? A Straight Answer.",
    excerpt:
      "Short version: no — checking your business loan rate with a broker is a soft inquiry and does not lower your FICO. Here's the longer version, plus exactly when a hard pull does happen.",
    date: "2026-01-10",
    readTime: "5 min read",
    category: "Credit",
    image:
      "https://images.pexels.com/photos/12471781/pexels-photo-12471781.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  body: `
## The 60-second answer

**No.** Getting a rate quote or pre-qualification from a business loan broker is a **soft credit inquiry**, and soft inquiries do not affect your FICO score. Ever. Not one point.

A **hard credit inquiry** happens only when you formally accept a loan offer and the lender moves to closing. That hard pull can move your score by 3–7 points and stays visible on your credit report for two years (though its impact fades within 12).

If a broker or lender tells you they need to hard-pull you just to "see if you qualify," walk away. That's not how modern business lending works in 2026.

## Soft vs. hard: the actual difference

Every credit inquiry falls into one of two buckets. The names sound similar but the impact is completely different.

| | Soft inquiry | Hard inquiry |
|---|---|---|
| Affects FICO? | No | Yes (typically −3 to −7 points) |
| Visible to lenders? | No | Yes for 24 months |
| Visible to you? | Yes (on your report) | Yes |
| Common triggers | Pre-qualification, background checks, self-pulls, credit monitoring | Formal loan application, mortgage, auto loan, credit card |
| Duration of impact | None | 12 months of noticeable impact, 24 months on report |

## When a soft inquiry happens (safe)

- You pre-qualify with a business loan broker or lender
- A lender checks your rate before extending a firm offer
- Your employer runs a background check
- A landlord or utility runs a rental check
- You check your own credit through Experian, FICO, or your card issuer's dashboard

None of these lower your score. You can pre-qualify with as many lenders as you want, on the same day, without moving your FICO a single point.

## When a hard inquiry happens (only when you say yes)

- You've reviewed an offer and formally accepted it
- The lender is moving to close and needs full credit verification
- You apply for a specific credit card, mortgage, or auto loan (there's no "pre-qualification" version of these — the application itself is the hard pull)
- You open a new checking account at some banks (rare, but check the fine print)

Notice the pattern: **hard inquiries require your active, informed consent.** A reputable lender will always tell you before pulling.

## The "shopping window" exception

For **mortgages, auto loans, and student loans**, FICO's scoring model treats multiple hard inquiries within a 14–45 day window as a single inquiry. This lets you shop rates across banks without penalty.

For **business loans, credit cards, and personal loans**, no such window exists — each hard inquiry counts separately. That's exactly why the industry moved to soft-pull pre-qualification: so you can compare offers before triggering hard pulls.

At Steady Path, we can shop your file across 75+ lenders with a single soft inquiry. Only when you accept a specific offer does that particular lender pull hard — one hard pull, one loan, one time.

## What actually moves your FICO (much more than an inquiry ever will)

Hard inquiries account for roughly **10% of your FICO score.** These four factors move it far more:

1. **Payment history** (35%) — one 30-day late payment costs more than every hard inquiry you'll take in your life
2. **Credit utilization** (30%) — paying down maxed cards from 85% to 30% utilization can move a score 30–70 points in a single month
3. **Length of credit history** (15%) — closing an old account can hurt more than opening a new one
4. **Credit mix + new credit** (20%) — a healthy mix of installment and revolving debt actually helps

If your goal is to protect or improve your score, focus on the top two categories. Inquiries are a rounding error by comparison.

## The three questions to ask any broker before you share your info

Before you submit any application, ask these three questions in writing:

1. **"Is this a soft credit pull or a hard credit pull?"** — Should be soft.
2. **"Will any lender hard-pull me before I've accepted an offer in writing?"** — Should be no.
3. **"Do you charge an application or credit check fee?"** — Should be no.

If any answer isn't the right one, you're talking to the wrong shop.

## The Steady Path promise

Applying with us is a soft credit inquiry only. Your FICO is not affected. You'll see real offers from real lenders side by side, and only when you accept one specific offer does that lender move to a hard pull to close.

That's the model. That's why we can promise "no impact to your credit to check your rate" — because it's actually true.

[Check your rate](/apply) — four minutes, no credit hit, no obligation.
`,
};

export default post;
